import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import twoFactorService, { TwoFactorStatus } from '../../services/twoFactorService';
import adminService, { AnalyticsClearResponse } from '../../services/adminService';

const TwoFactorSetup: React.FC = () => {
  const [status, setStatus] = useState<TwoFactorStatus>({ enabled: false });
  const [isLoading, setIsLoading] = useState(true);
  const [setupData, setSetupData] = useState<{
    qrCodeUrl?: string;
    backupCodes?: string[];
  }>({});
  const [step, setStep] = useState<'status' | 'setup' | 'verify' | 'complete'>('status');
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDisableConfirmation, setShowDisableConfirmation] = useState(false);
  const [action, setAction] = useState<'disable' | 'regenerate' | null>(null);

  // Data management state
  const [isClearing, setIsClearing] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [clearResult, setClearResult] = useState<AnalyticsClearResponse | null>(null);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setIsLoading(true);
      const statusData = await twoFactorService.getStatus();
      setStatus(statusData);
    } catch (error: any) {
      toast.error('Failed to load 2FA status');
      console.error('2FA status error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const startSetup = async () => {
    try {
      setIsSubmitting(true);
      const data = await twoFactorService.startSetup();
      setSetupData({
        qrCodeUrl: data.qrCodeUrl,
        backupCodes: data.backupCodes,
      });
      setStep('setup');
      toast.success('QR code generated! Scan with your authenticator app');
    } catch (error: any) {
      toast.error(error.message || 'Failed to start 2FA setup');
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifySetup = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    try {
      setIsSubmitting(true);
      await twoFactorService.verifySetup(verificationCode);
      setStep('complete');
      setStatus({ enabled: true, setupAt: new Date().toISOString() });
      toast.success('2FA enabled successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Invalid verification code');
      setVerificationCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const disable2FA = async () => {
    if (!password || !verificationCode) {
      toast.error('Please enter your password and 2FA code');
      return;
    }

    if (!showDisableConfirmation) {
      setShowDisableConfirmation(true);
      return;
    }

    try {
      setIsSubmitting(true);
      await twoFactorService.disable(password, verificationCode);
      setStatus({ enabled: false });
      setPassword('');
      setVerificationCode('');
      setStep('status');
      setShowDisableConfirmation(false);
      toast.success('2FA disabled successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to disable 2FA');
    } finally {
      setIsSubmitting(false);
    }
  };

  const regenerateBackupCodes = async () => {
    if (!verificationCode) {
      toast.error('Please enter your current 2FA code');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await twoFactorService.regenerateBackupCodes(verificationCode);
      setSetupData({ backupCodes: result.backupCodes });
      setStep('complete');
      toast.success('New backup codes generated');
    } catch (error: any) {
      toast.error(error.message || 'Failed to regenerate backup codes');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyBackupCodes = () => {
    if (setupData.backupCodes) {
      navigator.clipboard.writeText(setupData.backupCodes.join('\n'));
      toast.success('Backup codes copied to clipboard');
    }
  };

  const downloadBackupCodes = () => {
    if (setupData.backupCodes) {
      const content = `Two-Factor Authentication Backup Codes\nGenerated: ${new Date().toLocaleString()}\n\n${setupData.backupCodes.join('\n')}\n\nKeep these codes safe! Each can only be used once.`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '2fa-backup-codes.txt';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Backup codes downloaded');
    }
  };

  // Data management functions
  const handleClearAnalytics = async () => {
    if (!showClearConfirmation) {
      setShowClearConfirmation(true);
      return;
    }

    try {
      setIsClearing(true);
      setClearResult(null);

      const result = await adminService.clearAnalyticsData();
      setClearResult(result);
      setShowClearConfirmation(false);

      toast.success(`Successfully cleared ${result.summary.totalCleared} records!`);
    } catch (error: any) {
      console.error('Clear analytics error:', error);
      toast.error(error.message || 'Failed to clear analytics data');
    } finally {
      setIsClearing(false);
    }
  };

  const cancelClear = () => {
    setShowClearConfirmation(false);
    setClearResult(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center mb-6">
          <div className="text-2xl mr-3">🔐</div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h3>
            <p className="text-sm text-gray-600">
              Add an extra layer of security to your account
            </p>
          </div>
        </div>

        {/* Status Display */}
        {step === 'status' && (
          <div className="space-y-6">
            <div className={`p-4 rounded-lg border ${status.enabled
              ? 'bg-green-50 border-green-200'
              : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center">
                <div className="text-lg mr-3">
                  {status.enabled ? '✅' : '⚠️'}
                </div>
                <div>
                  <div className="font-medium">
                    {status.enabled ? '2FA is enabled' : '2FA is disabled'}
                  </div>
                  <div className="text-sm text-gray-600">
                    {status.enabled
                      ? `Enabled on ${status.setupAt ? new Date(status.setupAt).toLocaleDateString() : 'unknown date'}`
                      : 'Your account is not protected by two-factor authentication'
                    }
                  </div>
                </div>
              </div>
            </div>

            {!status.enabled ? (
              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  <p className="mb-2">Two-factor authentication adds an extra layer of security by requiring:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Your password</li>
                    <li>A 6-digit code from your phone (Google Authenticator, Authy, etc.)</li>
                  </ul>
                </div>
                <button
                  onClick={startSetup}
                  disabled={isSubmitting}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Setting up...' : 'Enable Two-Factor Authentication'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => {
                      setAction('regenerate');
                      setStep('verify');
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Regenerate Backup Codes
                  </button>
                  <button
                    onClick={() => {
                      setAction('disable');
                      setStep('verify');
                      setPassword('');
                      setVerificationCode('');
                      setShowDisableConfirmation(false);
                    }}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                  >
                    Disable 2FA
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Setup - QR Code */}
        {step === 'setup' && setupData.qrCodeUrl && (
          <div className="space-y-6">
            <div className="text-center">
              <h4 className="text-lg font-medium mb-4">Scan QR Code</h4>
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
                <img
                  src={setupData.qrCodeUrl}
                  alt="2FA QR Code"
                  className="w-48 h-48"
                />
              </div>
              <p className="text-sm text-gray-600 mt-4 max-w-md mx-auto">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.),
                then enter the 6-digit code below to verify.
              </p>
            </div>
            <button
              onClick={() => setStep('verify')}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              I've Scanned the QR Code
            </button>
          </div>
        )}

        {/* Verification */}
        {step === 'verify' && (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-medium mb-4">
                {status.enabled ? 'Enter Current 2FA Code' : 'Verify Setup'}
              </h4>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                6-digit code from your authenticator app
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-lg tracking-wider"
                placeholder="000000"
                maxLength={6}
              />
            </div>

            {status.enabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current password (required to disable)
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter your password"
                />
              </div>
            )}

            {/* Confirmation UI for disabling 2FA */}
            {action === 'disable' && showDisableConfirmation && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-2">
                  <div className="text-red-500 text-lg mr-2">⚠️</div>
                  <div className="font-medium text-red-800">Confirm Disable 2FA</div>
                </div>
                <p className="text-sm text-red-700 mb-3">
                  Are you sure you want to disable two-factor authentication? This will make your account less secure.
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowDisableConfirmation(false)}
                    className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={disable2FA}
                    disabled={isSubmitting}
                    className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Disabling...' : 'Yes, Disable 2FA'}
                  </button>
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setStep('status');
                  setAction(null);
                  setShowDisableConfirmation(false);
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={
                  action === 'regenerate'
                    ? regenerateBackupCodes
                    : action === 'disable'
                    ? disable2FA
                    : verifySetup
                }
                disabled={isSubmitting || !verificationCode}
                className={`flex-1 px-4 py-2 rounded-md text-white disabled:opacity-50 ${
                  action === 'disable'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSubmitting
                  ? 'Processing...'
                  : action === 'regenerate'
                  ? 'Regenerate Codes'
                  : action === 'disable'
                  ? 'Disable 2FA'
                  : 'Verify & Enable'
                }
              </button>
            </div>
          </div>
        )}

        {/* Complete - Backup Codes */}
        {step === 'complete' && setupData.backupCodes && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-4xl mb-2">🎉</div>
              <h4 className="text-lg font-medium text-green-600 mb-2">
                Success! 2FA is now enabled
              </h4>
              <p className="text-sm text-gray-600">
                Save these backup codes in a safe place. Each can only be used once.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Backup Codes:</div>
              <div className="grid grid-cols-1 gap-1 font-mono text-sm">
                {setupData.backupCodes.map((code, index) => (
                  <div key={index} className="bg-white px-3 py-1 rounded border">
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={copyBackupCodes}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                📋 Copy Codes
              </button>
              <button
                onClick={downloadBackupCodes}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
              >
                💾 Download
              </button>
            </div>

            <button
              onClick={() => {
                setStep('status');
                setSetupData({});
              }}
              className="w-full bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
            >
              Done
            </button>
          </div>
        )}
      </div>

      {/* Data Management Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
        <div className="border-l-4 border-orange-500 pl-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            🗂️ Production Data Management
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Clear analytics and conversation data while preserving your configuration
          </p>
        </div>

        {/* Explanation */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-blue-800 mb-2">What this does:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h5 className="font-medium text-blue-700 mb-1">📊 Will Clear:</h5>
              <ul className="text-blue-600 space-y-1">
                <li>• Visitor tracking data</li>
                <li>• Conversation history</li>
                <li>• Lead submissions</li>
                <li>• Download analytics</li>
                <li>• Segment assignments</li>
              </ul>
            </div>
            <div>
              <h5 className="font-medium text-green-700 mb-1">✅ Will Preserve:</h5>
              <ul className="text-green-600 space-y-1">
                <li>• Admin accounts</li>
                <li>• Knowledge base content</li>
                <li>• Resources and files</li>
                <li>• Widget settings</li>
                <li>• Segment definitions</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Clear Analytics Action */}
        {!showClearConfirmation && !clearResult && (
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">
              Perfect for production testing - gives you a clean slate without losing your valuable configuration.
            </p>
            <button
              onClick={handleClearAnalytics}
              disabled={isClearing}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isClearing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Clearing Data...
                </>
              ) : (
                <>
                  🗑️ Clear Analytics Data
                </>
              )}
            </button>
          </div>
        )}

        {/* Confirmation Dialog */}
        {showClearConfirmation && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <div className="text-red-500 text-xl mr-3">⚠️</div>
              <h4 className="font-medium text-red-800">Confirm Data Clearing</h4>
            </div>
            <div className="text-red-700 mb-4 space-y-2">
              <p className="font-medium">Are you absolutely sure?</p>
              <p className="text-sm">
                This will permanently delete all conversation history, lead submissions, and analytics data.
                This action <strong>cannot be undone</strong>.
              </p>
              <p className="text-sm">
                Your configuration (admin accounts, knowledge base, resources, settings) will be preserved.
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={cancelClear}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleClearAnalytics}
                disabled={isClearing}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isClearing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Clearing...
                  </>
                ) : (
                  'Yes, Clear All Data'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Success Result */}
        {clearResult && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <div className="text-green-500 text-xl mr-3">✅</div>
              <h4 className="font-medium text-green-800">Data Cleared Successfully</h4>
            </div>
            <div className="text-green-700 mb-4">
              <p className="mb-2">{clearResult.message}</p>
              <div className="text-sm bg-white rounded p-3 border border-green-200">
                <h5 className="font-medium mb-2">Summary:</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div>Visitors: {clearResult.summary.visitors}</div>
                  <div>Conversations: {clearResult.summary.conversations}</div>
                  <div>Leads: {clearResult.summary.leads}</div>
                  <div>Downloads: {clearResult.summary.resourceDownloads}</div>
                  <div>Tokens: {clearResult.summary.downloadTokens}</div>
                  <div className="font-medium col-span-2 pt-2 border-t border-green-200">
                    Total Cleared: {clearResult.summary.totalCleared} records
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => setClearResult(null)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorSetup;